import { useUpdateOrganisationDefaultSettingsMutation } from '@/app/redux/endpoints/organisation';
import { useBulkUpdateStudentSettingsMutation } from '@/app/redux/endpoints/students';
import { useAppDispatch } from '@/app/redux/hooks';
import { useOrganisation } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import { useUser } from '@/app/redux/hooks/user';
import {
  openConfirmationDialogue,
  setConfirmationDialogueCallback,
} from '@/app/redux/slices/modalsSlice';
import {
  AudioDelay,
  ControllerType,
  GameSettings,
} from '@/common/types/student';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { initialGameSettings } from '@/common/utils/student';
import LearnerSettings from '@/dashboard/LearnerView/LearnerSettings';
import {
  SettingsContentContainer,
  SettingsHeaderContainer,
  SettingsHeaderText,
  SettingsPageContainer,
} from '@/dashboard/SettingsView/SettingsPageStyles';
import {
  ArrowCounterClockwiseIcon,
  CheckIcon,
  ChecksIcon,
} from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { chunk, isEqual } from 'lodash';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';

export default function OrgWideLearnerSettings() {
  const dispatch = useAppDispatch();

  const { user } = useUser();
  const { organisation } = useOrganisation();

  const { students } = useStudents();
  const [bulkUpdateStudentSettings] = useBulkUpdateStudentSettingsMutation();
  const [updateOrganisationDefaultSettings] =
    useUpdateOrganisationDefaultSettingsMutation();

  const [isSaving, setIsSaving] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [settings, setSettings] = useState<GameSettings>(
    organisation?.defaultStudentSettings ?? initialGameSettings,
  );

  const hasChanged = useMemo(() => {
    return !isEqual(
      settings,
      organisation?.defaultStudentSettings ?? initialGameSettings,
    );
  }, [settings, organisation]);

  const needsApplyToExisting = useMemo(() => {
    return students.some((student) => !isEqual(student.settings, settings));
  }, [students, settings]);

  function handlePlayPhonemeChanged(value: boolean) {
    setSettings({ ...settings, playPhonemeOnCardDrop: value });
  }
  function handleControllerTypeChange(value: ControllerType) {
    setSettings({ ...settings, controllerType: value });
  }
  function handleDuelsEnabledChange(value: boolean) {
    setSettings({ ...settings, duelsEnabled: value });
  }
  function handleAudioDelayChange(value: AudioDelay) {
    setSettings({
      ...settings,
      audio: { ...settings.audio, audioDelay: value },
    });
  }
  function handleDistractorCardsChange(value: boolean) {
    setSettings({ ...settings, distractorCardsEnabled: value });
  }
  function handleWordLimitChange(value: number) {
    setSettings({ ...settings, wordLimit: value });
  }
  function handleWordSegmentationChange(value: boolean) {
    setSettings({
      ...settings,
      wordSegmentationEnabled: value,
      audio: {
        ...settings.audio,
        audioDelay: value ? settings.audio.audioDelay : AudioDelay.SHORT,
      },
    });
  }
  function handleArticulatoryGesturesChange(value: boolean) {
    setSettings({ ...settings, articulatoryGesturesEnabled: value });
  }
  function handleRevertToDefaults() {
    setSettings(initialGameSettings);
  }

  async function handleApplyToExistingLearners() {
    if (!organisation) {
      console.error('Missing organisation');
      toast.error('Failed to save settings', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }

    setIsApplying(true);
    const studentIds = students.map((s) => s.id);
    const chunks = chunk(studentIds, 50);
    let applied = 0;

    const toastId = toast.loading('Applying settings...', {
      description: "Please don't refresh or leave the page",
    });

    try {
      for (const idChunk of chunks) {
        await bulkUpdateStudentSettings({
          studentIds: idChunk,
          userName: user?.name ?? 'Educator',
          organisationId: organisation.id,
          settings,
          location: 'org-wide-settings',
        });
        applied += idChunk.length;
        toast.loading(`Applying settings (${applied}/${studentIds.length})`, {
          id: toastId,
        });
      }

      toast.success('Settings applied to existing learners', {
        id: toastId,
        description: '',
      });
      trackMixpanelEvent(
        'Organisation Default Settings Applied to Existing Learners',
        {
          updatedSettings: settings,
        },
      );
    } catch (e) {
      console.error('Error applying settings to existing learners:', e);
      if (applied > 0) {
        toast.error('Failed to apply settings', {
          id: toastId,
          description: `Applied to ${applied} of ${studentIds.length} learners. Please retry.`,
        });
      } else {
        toast.error('Failed to apply settings', {
          id: toastId,
          description: UNKNOWN_ERROR_MESSAGE,
        });
      }
    } finally {
      setIsApplying(false);
    }
  }

  function handleConfirmSave() {
    setConfirmationDialogueCallback(handleSave);
    dispatch(
      openConfirmationDialogue({
        title: 'Save Default Learner Settings',
        description:
          'Do you want to apply these settings to existing learners?',
        checkboxOption: {
          label: 'Apply to existing learners',
          default: true,
        },
      }),
    );
  }

  async function handleSave(applyToExisting: boolean) {
    if (!organisation) {
      console.error('Missing org ID');
      toast.error('Failed to save settings', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
      return;
    }
    try {
      setIsSaving(true);
      await updateOrganisationDefaultSettings({
        orgId: organisation.id,
        updates: settings,
      }).unwrap();
      toast.success('Settings saved');
      trackMixpanelEvent('Organisation Default Settings Updated', {
        organisationId: organisation.id,
        updatedSettings: settings,
      });
      if (applyToExisting) {
        await handleApplyToExistingLearners();
      }
    } catch (e) {
      console.error('Error saving settings:', e);
      toast.error('Failed to save settings', {
        description: UNKNOWN_ERROR_MESSAGE,
      });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <SettingsPageContainer>
      <SettingsHeaderContainer>
        <SettingsHeaderText>
          <h2>Default Learner Settings</h2>
          <div>
            These settings will be applied to new learners created in this
            organisation. To apply these settings to existing learners, use the
            "Apply to existing learners" button below.
          </div>
        </SettingsHeaderText>
      </SettingsHeaderContainer>
      <SettingsContentContainer>
        <div className="w-full h-full flex flex-col gap-6 mb-4">
          <LearnerSettings
            settings={settings}
            onPlayPhonemeChanged={handlePlayPhonemeChanged}
            onControllerTypeChange={handleControllerTypeChange}
            onDuelsEnabledChange={handleDuelsEnabledChange}
            onAudioDelayChange={handleAudioDelayChange}
            onDistractorCardsChange={handleDistractorCardsChange}
            onWordLimitChange={handleWordLimitChange}
            onWordSegmentationChange={handleWordSegmentationChange}
            onArticulatoryGesturesChange={handleArticulatoryGesturesChange}
            disabled={isSaving || isApplying}
            largeLabels
          />
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-end gap-2">
              <Button
                variant="outline"
                className="gap-2"
                onClick={handleRevertToDefaults}
                disabled={isSaving || isApplying}
              >
                <ArrowCounterClockwiseIcon size={16} />
                Restore defaults
              </Button>
              <Button
                variant="outline"
                className="gap-2"
                onClick={handleApplyToExistingLearners}
                disabled={isSaving || !needsApplyToExisting}
                loading={isApplying}
              >
                <ChecksIcon size={16} />
                Apply to existing learners
              </Button>
              <Button
                className="gap-2"
                onClick={handleConfirmSave}
                disabled={!hasChanged || isApplying}
                loading={isSaving}
              >
                <CheckIcon size={16} />
                Save
              </Button>
            </div>
          </div>
        </div>
      </SettingsContentContainer>
    </SettingsPageContainer>
  );
}
